import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Link } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import CircleImage from '../../components/CircleImage';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';

// ICONS
import { ReactComponent as ValidatedIcon } from '../../assets/svg/shield-color.svg';
import { ReactComponent as InValidatedIcon } from '../../assets/svg/shield-grey.svg';
import { ReactComponent as SeachIcon } from '../../assets/svg/search.svg';

// TYPES
import { FamiliesDashboardFamilyMember, DomainProfileType } from '../../api/models';

const Families: React.FC = () => {
	// Check if User is Organisation Admin
	const isOrganisationAdmin = useAppSelector((state) => state.user.isOrganisationAdmin);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Set up data state
	const [familiesData, setFamiliesData] = React.useState<FamiliesDashboardFamilyMember[]>([]);

	// Data used to search, it will not be changed and will always remain as its inital set value
	const [originalFamiliesData, setOriginalFamiliesData] = React.useState<FamiliesDashboardFamilyMember[]>([]);

	// function to  Get Families data from api call
	const getFamiliesData = async () => {
		setIsLoading(true);

		const response = await apibridge.getFamiliesData();

		response.result = response.result || [];

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			setFamiliesData(response.result);

			setOriginalFamiliesData(response.result);
		}
	};

	React.useEffect(() => {
		getFamiliesData();
	}, []);

	const parentsData = familiesData.filter(
		(item) => item.profileType === DomainProfileType.Parent && item.isCurrentMember === true
	);

	const studentData = familiesData.filter(
		(item) => item.profileType === DomainProfileType.Student && item.isCurrentMember === true
	);

	const parentsVolunteersData = parentsData.filter(
		(item) => item.isVolunteer === true && item.isVolunteerValidated === true && item.isCurrentMember === true
	);

	// State to control active tab and active content
	const [activeTab, setActiveTab] = React.useState<string>('tab1');
	const [searchValue, setSearchValue] = React.useState<string>('');

	// Filter Function
	const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newSearchValue = e.target.value;

		if (!newSearchValue) {
			setFamiliesData(originalFamiliesData);
		} else {
			const currentOriginalFamiliesData = originalFamiliesData.filter((item) => item.isCurrentMember === true);

			const newSearchResult = currentOriginalFamiliesData.filter(
				(data) =>
					(data.firstName && data.firstName.toLowerCase().includes(newSearchValue.toLowerCase())) ||
					(data.lastName && data.lastName.toLowerCase().includes(newSearchValue.toLowerCase()))
			);

			setFamiliesData(newSearchResult);
		}

		setSearchValue(newSearchValue);
	};

	return (
		<div className="page-general-layout families-page">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<nav className="page-header-section families-page">
									<div className="header-left">
										<Button
											onClick={() => setActiveTab('tab1')}
											className={`staff-category pdl-0 ${activeTab === 'tab1' ? 'active' : ''}`}
										>
											Parents
										</Button>

										<Button
											onClick={() => setActiveTab('tab2')}
											className={`staff-category ${activeTab === 'tab2' ? 'active' : ''}`}
										>
											Children & Students
										</Button>

										<Button
											onClick={() => setActiveTab('tab3')}
											className={`staff-category ${activeTab === 'tab3' ? 'active' : ''}`}
										>
											Parent Volunteers
										</Button>
									</div>

									<div className="header-right">
										<div className="seach-container">
											<SeachIcon className="search-icon" />

											<input onChange={onChangeHandler} type="text" placeholder="Search" value={searchValue} />
										</div>
									</div>
								</nav>

								<section className="info-section mtop-40 mbtom-30">
									{activeTab === 'tab1' && (
										<ul>
											{isOrganisationAdmin &&
												parentsData &&
												parentsData.length > 0 &&
												parentsData.map((item) => {
													return (
														<li key={item.id} className="row-container">
															<div className="row detail-row">
																<div className="col-auto pdl-0 pdr-0 img-container">
																	<CircleImage
																		firstName={item.firstName}
																		lastName={item.lastName}
																		src={item.profileImage}
																		isProfileInComplete={item.needsAttention}
																	/>
																</div>

																<div className="col-md-auto full-width info-container align-self-center">
																	{item.isVolunteerValidated ? <ValidatedIcon className="mr-12" /> : null}
																	{item.firstName} {item.lastName}
																</div>

																<div className="col-auto pdr-0 link-group">
																	<Link className="btn-text mtop-0 pdr-0" to={`/families/${item.id}`}>
																		View
																	</Link>
																</div>
															</div>
														</li>
													);
												})}
										</ul>
									)}

									{activeTab === 'tab2' && (
										<ul>
											{isOrganisationAdmin &&
												studentData &&
												studentData.length > 0 &&
												studentData.map((item) => {
													return (
														<li key={item.id} className="row-container">
															<div className="row detail-row">
																<div className="col-auto pdl-0 pdr-0 img-container">
																	<CircleImage
																		firstName={item.firstName}
																		lastName={item.lastName}
																		src={item.profileImage}
																		isProfileInComplete={item.needsAttention}
																	/>
																</div>

																<div className="col-md-auto full-width info-container align-self-center">
																	{item.firstName} {item.lastName}
																</div>

																<div className="col-auto pdr-0 link-group">
																	<Link className="btn-text mtop-0 pdr-0" to={`/families/${item.id}`}>
																		View
																	</Link>
																</div>
															</div>
														</li>
													);
												})}
										</ul>
									)}

									{activeTab === 'tab3' && (
										<ul>
											{isOrganisationAdmin &&
												parentsVolunteersData &&
												parentsVolunteersData.length > 0 &&
												parentsVolunteersData.map((item) => {
													return (
														<li key={item.id} className="row-container">
															<div className="row detail-row">
																<div className="col-auto pdl-0 pdr-0 img-container">
																	<CircleImage
																		firstName={item.firstName}
																		lastName={item.lastName}
																		src={item.profileImage}
																		isProfileInComplete={item.needsAttention}
																	/>
																</div>

																<div className="col-md-auto full-width">
																	<div className="row h-100">
																		<div className="col-12 col-lg-6 info-container align-self-center">
																			{item.firstName} {item.lastName}
																		</div>

																		<div className="col-12 col-lg-6 align-self-center">
																			{item.isVolunteerValidated ? (
																				<div className="validation-message">
																					<ValidatedIcon />
																					Parent Volunteer
																				</div>
																			) : (
																				<div className="validation-message">
																					<InValidatedIcon />
																					Parent Volunteer: Validation Required
																				</div>
																			)}
																		</div>
																	</div>
																</div>

																<div className="col-auto pdr-0 link-group">
																	<Link className="btn-text pdr-0" to={`/families/${item.id}`}>
																		View
																	</Link>
																</div>
															</div>
														</li>
													);
												})}
										</ul>
									)}
								</section>

								<ScrollToTopButton />
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default Families;
